import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useEvent } from "../context/EventProvider";
import { sameSession } from "../auth/session";
import { useSessionKey } from "../auth/useSessionKey";
import { useAnswers } from "../funnel/answers";
import {
  addCompanion,
  carrierHeritage,
  writeDays,
  fetchCompanions,
  forgetCompanionReads,
  isOwnEmailRefusal,
  removeCompanion,
  writeCompanionTypo,
  type Companion,
} from "../funnel/companions";
import {
  attach,
  fetchCompanionCompanies,
  fetchMyCompanies,
} from "../funnel/companies";
import { companionCompany } from "../funnel/companionCompany";
import { CompanionSteps } from "../funnel/CompanionSteps";
import { attachCoworker, type Coworker } from "../funnel/coworkers";
import { fetchParticipation } from "../funnel/participant";
import { CoworkerPicker } from "../funnel/CoworkerPicker";
import { useCoworkers } from "../funnel/useCoworkers";
import { companionSteps } from "../funnel/companionThread";
import { saveAnswers, type Field } from "../funnel/forms";
import { useArticles } from "../funnel/useArticles";
import { useCompanions } from "../funnel/useCompanions";
import { useCompany } from "../funnel/useSociete";
import { useChosenForm, useWords } from "../funnel/useForm";
import { useFunnelNav } from "../funnel/useFunnelNav";
import { useParticipation } from "../funnel/useParticipation";
import { fallbackOf } from "../settings/catalog";
import { ApiError } from "../api/client";
import { useLocale, useSettings, useT, useText } from "../settings/context";
import { Button } from "../ui/Button";
import { Icon } from "../ui/Icon";
import { Confirm } from "../ui/Confirm";
import { Prose } from "../ui/Prose";
import { sendActionEmail } from "../emails/send";
import { InPlace } from "../panel/InPlace";
import { useMode } from "../panel/mode";
import { logAction } from "../log/actionLog";

/**
 * L'ÉTAPE ACCOMPAGNANTS — qui vient avec vous, et de quoi en ajouter.
 *
 * ⚠️ SON PARCOURS EST DÉCORRÉLÉ DU VÔTRE : ses étapes ont leurs propres interrupteurs et
 * leur propre ordre — le formulaire d'abord, parce que c'est lui qui CRÉE la personne
 * dont les étapes suivantes ont besoin. Voir `funnel/companionThread.ts`.
 *
 * ⚠️ ET IL S'ÉCRIT TOUT DE SUITE, à la différence des réponses du porteur qui attendent
 * la confirmation. Un accompagnant n'est pas une réponse : c'est un COMPTE, avec son
 * badge et sa place. Le garder en mémoire jusqu'au bout obligerait à tout créer d'un
 * coup à la fin — et un échec à mi-chemin laisserait la moitié des gens inscrits sans
 * que personne sache lesquels.
 *
 * Displayed strings go through `t()`, French kept at the call site as the
 * fallback: what the site says of itself follows the language being read.
 */

const ICI = "/registration/companions";

/** Les clés que le produit range sur la PERSONNE, et dont la création a besoin. */
const IDENTITY = { firstName: "firstName", name: "lastName", email: "email" };

/**
 * ⚠️ SERVI AUSSI COMME PAGE (`/companions`) — voir `pages/stepPages.ts`. Chaque
 * accompagnant s'enregistre au moment où on l'ajoute : hors du tunnel, il ne reste donc
 * RIEN à valider, et la rangée « Retour / Continuer » disparaît entièrement plutôt que
 * de proposer un bouton qui ne ferait que naviguer.
 */
export function Companions({ asPage }: { asPage?: true }) {
  const t = useT();
  const { value, days } = useSettings();
  const event = useEvent();
  const client = useQueryClient();
  const { me, recorded } = useParticipation();
  const sessionKey = useSessionKey();
  /* Ses choix du parcours en cours, pas encore écrits : à la première inscription, ce
     sont les SEULS qui existent (voir `carrierHeritage`). */
  const [answers] = useAnswers();
  /** Le porteur, tel que les emails d'accompagnant le nomment (`{{var:fromUser}}`). */
  const fromUser = () =>
    [me?.firstName, me?.lastName].filter(Boolean).join(" ").trim() ||
    me?.email ||
    "";
  const locale = useLocale();

  const title = useText(
    "FO_MEETING_V2_COMPANIONS_TITLE",
    fallbackOf("FO_MEETING_V2_COMPANIONS_TITLE"),
  );
  const subtitle = useText(
    "FO_MEETING_V2_COMPANIONS_SUBTITLE",
    fallbackOf("FO_MEETING_V2_COMPANIONS_SUBTITLE"),
  );
  const html = useText("FO_MEETING_V2_COMPANIONS_HTML");

  const { companions, loading } = useCompanions(true);

  /**
   * LE PLAFOND.
   *
   * ⚠️ ZÉRO ET L'ABSENCE VEULENT DIRE « PAS DE LIMITE », et non « aucun » : un réglage
   * numérique jamais renseigné vaut zéro, et le lire comme un plafond fermerait l'étape
   * sur tous les événements qui ne l'ont pas rempli — c'est-à-dire presque tous.
   */
  const raw = Number(value("FO_MEETING_V2_COMPANIONS_MAX"));
  const cap = Number.isFinite(raw) && raw > 0 ? raw : undefined;
  const full = cap !== undefined && companions.length >= cap;

  const { previous, next, go } = useFunnelNav(ICI);

  /**
   * ═══ À DISTANCE, ON N'INSCRIT PERSONNE ═══ (tâche #225)
   *
   * Le fil retire déjà l'étape (`funnel/thread.ts`), mais la PAGE « Mes accompagnants »
   * vit hors du fil : sans cette garde, un inscrit à distance y ajoutait encore quelqu'un.
   * On le dit, et l'on n'offre ni l'ajout ni la modification. Le retrait reste : qui est
   * passé de « sur place » à « à distance » doit pouvoir désinscrire ceux qu'il amenait.
   *
   * ⚠️ JAMAIS POUR L'ORGANISATEUR, qui ouvre cet écran pour le régler, quelle que soit sa
   * propre réponse.
   */
  const remote =
    useMode() === "visitor" &&
    (answers.participation ?? recorded?.answer) === "remote";

  /**
   * ═══ SES COLLABORATEURS, À CHOISIR PLUTÔT QU'À RESSAISIR ═══
   *
   * La V1 le propose sous `FO_MEETING_COMPANIONS_ASSOCIATES_IS_ACTIVE`
   * (`screens/steps/Confirmation/addAssociates.jsx`) : la liste des personnes de ses
   * sociétés, un « Choisir » par ligne, grisé pour qui est déjà inscrit, et la saisie à
   * la main toujours possible à côté. Voir `funnel/coworkers.ts` pour qui est listé.
   *
   * ⚠️ NI À DISTANCE NI PLAFOND ATTEINT : on n'y inscrit personne, la liste n'a pas à
   * être demandée.
   */
  const offersCoworkers =
    value("FO_MEETING_V2_COMPANIONS_COWORKERS") === true && !remote;
  /**
   * ═══ THE PEOPLE ONE ADDS COME UNDER ONE OF ONE'S COMPANIES ═══
   *
   * Always when coworkers are offered: they are coworkers. Otherwise when the organiser
   * asks for it (`FO_MEETING_V2_COMPANIONS_COMPANY`), on an event where companions come
   * on behalf of a company without being picked from a list. One company: automatic; several:
   * a step to choose, first (`COMPANY_STEP`). Never at a distance: nobody is added then.
   */
  const attachesCompany =
    (offersCoworkers || value("FO_MEETING_V2_COMPANIONS_COMPANY") === true) &&
    !remote;
  const {
    coworkers,
    loading: coworkersLoading,
    failed: coworkersFailed,
  } = useCoworkers(offersCoworkers && !full);
  const companionIds = new Set(companions.map((c) => c.id));

  /**
   * ═══ CE QUE L'ACCOMPAGNANT TRAVERSERA ═══
   *
   * Calculé ici pour le dire AVANT d'ouvrir le formulaire : on annonce ce qu'on va
   * demander.
   *
   * ⚠️ SA BILLETTERIE N'A DE SUJET QUE SI L'ÉVÉNEMENT VEND QUELQUE CHOSE, et cette
   * moitié de la règle ne s'appliquait jamais : `companionSteps` sait retirer l'étape,
   * son test le vérifie, mais personne ne lui a jamais dit s'il y avait des articles. On
   * traversait donc une étape qui n'annonçait que « Rien à proposer pour les jours
   * retenus », sur des événements qui ne vendent rien — c'est-à-dire l'immense majorité.
   *
   * ⚠️ LE CATALOGUE N'EST DEMANDÉ QUE SI L'INTERRUPTEUR EST ALLUMÉ, et c'est ce qui rend
   * la requête acceptable : sur un événement dont l'organisateur n'a pas ouvert cette
   * étape, il n'y a rien à demander — le premier calcul suffit à le savoir. Il coûte une
   * lecture, partagée avec l'étape billetterie du porteur (même clé, même cache).
   *
   * ⚠️ ET TANT QU'ON NE SAIT PAS, ON NE RETIRE RIEN. `undefined` vaut « je ne sais pas »
   * pour ce sac (voir `Offre`), et c'est exactement ce qu'il faut ici : lu à `[]` pendant
   * que la requête est en vol, l'étape aurait disparu du fil pour y revenir une seconde
   * plus tard — le fil se renumérote sous les yeux du porteur, qui a lu autre chose.
   */
  const requested = companionSteps(value, { days: days }).some(
    (step) => step.key === "articles",
  );
  const { articles, loading: catalogueEnCours } = useArticles(
    requested,
    locale,
  );
  /**
   * ═══ A COWORKER AMONG SEVERAL COMPANIES OF THE BEARER: A STEP TO CHOOSE, FIRST ═══
   *
   * See `COMPANY_STEP` in `funnel/companionThread.ts`. The bearer's companies are the
   * same read, and the same cache, as their own company step, earlier in the funnel.
   *
   * ⚠️ NO JOURNEY OPENS BEFORE THEY ARE KNOWN (`companiesPending`). The step comes FIRST:
   * arriving after the opening, it would shift every index under the bearer, who would
   * find themselves one step back, on a question they had not been asked.
   */
  const bearerCompanies = useCompany(attachesCompany);
  const companiesPending =
    attachesCompany && !bearerCompanies.fetched && !bearerCompanies.failure;
  const theirFunnel = companionSteps(value, {
    days: days,
    ...(requested && !catalogueEnCours
      ? { articles: articles.length > 0 }
      : {}),
    companyChoice: attachesCompany && bearerCompanies.companies.length > 1,
  });
  /** Where someone who already exists reopens: their form, the company step behind it. */
  const informationsAt = Math.max(
    0,
    theirFunnel.findIndex((s) => s.key === "informations"),
  );

  /**
   * ⚠️ SON FORMULAIRE, AVEC CELUI DU PORTEUR EN REPLI — la règle de la V1
   * (`FO_MEETING_COMPANION_FORM_ID || settings.formId`). Un événement qui n'en a pas
   * réglé de spécifique demande la même chose à tout le monde ; celui qui en règle un
   * peut n'exiger qu'un nom.
   *
   * ⚠️ LE REPLI SE LISAIT ICI AVEC UN `??`, ET IL NE SE DÉCLENCHAIT DONC JAMAIS après
   * coup : le panneau écrit la CHAÎNE VIDE quand on remet le sélecteur sur « — », que
   * `??` laisse passer. L'étape ouvrait alors un formulaire sans champs, donc sans nom
   * à lire, et refusait l'ajout sur « Indiquez au moins un nom » — sans champ pour en
   * donner un. Voir `useFormChoisi`.
   */
  const form = useChosenForm(
    "FO_MEETING_V2_COMPANION_FORM",
    "FO_MEETING_V2_INFO_FORM_ID",
  );
  const fields: Field[] = form?.fields ?? [];
  const words = useWords(form);

  const [sending, setSending] = useState(false);
  /**
   * LE REFUS DU MOMENT, ET LA VUE QUI VA LE CHERCHER.
   *
   * ⚠️ IL S'AFFICHE EN HAUT, ET L'ON CLIQUE EN BAS. « Continuer » est sous le formulaire
   * de l'accompagnant, souvent long : sa propre adresse refusée (`COMPANION_IS_YOU`)
   * s'écrivait hors de la vue, et rien ne bougeait. Mesuré : le bloc à -482 px dans
   * `.scroll`, aucun message visible. La vue y remonte donc à chaque refus.
   *
   * ⚠️ UN OBJET NEUF À CHAQUE FOIS, pas la phrase seule : le même refus deux fois de
   * suite (« Indiquez au moins un nom », effacé puis reposé dans le même clic) laisse
   * une chaîne identique, que React ne voit pas changer, et la vue ne bougeait plus au
   * second clic.
   */
  const [rate, setRate] = useState<{ text: string } | null>(null);
  const refuse = (text: string) => setRate({ text });
  const rateRef = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    rateRef.current?.scrollIntoView({ block: "center" });
  }, [rate]);
  const [toRemove, setToRemove] = useState<Companion | null>(null);
  /**
   * THE COWORKERS WINDOW IS OPEN (`funnel/CoworkerPicker`), and `choosing` is the
   * coworker being attached from it, whose line spins.
   *
   * ⚠️ ALWAYS A WINDOW, WHATEVER THEIR NUMBER: listed in the step, a hundred coworkers
   * made a hundred cards before « Ajouter une personne ».
   */
  const [picking, setPicking] = useState(false);
  const [choosing, setChoosing] = useState<number | null>(null);

  /**
   * LA PERSONNE EN COURS, ET OÙ L'ON EN EST DANS SON PARCOURS.
   *
   * ⚠️ ELLE EXISTE DÉJÀ QUAND CET ÉTAT S'OUVRE : le formulaire l'a créée, avec son
   * compte et son badge. Ce qui suit ne fait que la compléter. S'arrêter en chemin est
   * sans danger : elle reste inscrite avec ce qu'on lui a déjà donné, et l'on peut y
   * revenir. D'où « Annuler », à toutes les étapes (bug #339) : il referme le parcours
   * sans rien écrire, et sans rien défaire non plus.
   */
  const [enCours, setEnCours] = useState<{
    /** `null` tant que la personne n'existe pas : c'est la première étape qui la crée. */
    id: number | null;
    name: string;
    step: number;
    /** Créée dans ce parcours, et non rouverte par « Modifier » : le journal note l'ajout. */
    created?: boolean;
  } | null>(null);

  /**
   * CRÉE LA PERSONNE — appelée par la PREMIÈRE étape de son parcours.
   *
   * ⚠️ ELLE VIT ICI, PAS DANS LE PARCOURS : créer touche à la liste, au plafond et à ce
   * que la confirmation rappellera. C'est l'affaire de l'étape, qui en est responsable ;
   * le parcours, lui, recueille le formulaire et demande.
   *
   * Rend l'identifiant, ou `null` si rien n'a pu être inscrit — auquel cas le parcours
   * reste sur son formulaire au lieu d'avancer vers des étapes sans sujet.
   */
  const create = async (
    values: Record<string, unknown>,
  ): Promise<number | null> => {
    if (!event || typeof me?.id !== "number") return null;
    // ⚠️ Session changée depuis le rendu (autre onglet) : ce qui est ici est à l'ancien compte, rien ne part.
    if (!sameSession(sessionKey)) return null;
    setRate(null);

    const read = (key: string) => {
      const field = fields.find((f) => f.model === "U" && f.key === key);
      const v = field ? values[field.id] : undefined;
      return typeof v === "string" ? v.trim() : "";
    };
    const firstName = read(IDENTITY.firstName);
    const name = read(IDENTITY.name);
    const email = read(IDENTITY.email);

    /* ⚠️ LE NOM SUFFIT, PAS L'ADRESSE. Le produit accepte un accompagnant sans compte —
       un conjoint, un chauffeur — et il a quand même son badge. */
    if (!firstName && !name) {
      refuse(
        t(
          "COMPANION_NAME_REQUIRED",
          "Indiquez au moins un nom pour cette personne.",
        ),
      );
      return null;
    }

    try {
      const cree = await addCompanion(event.domainId, event.eventId, me.id, {
        firstName: firstName,
        lastName: name,
        ...(email ? { email } : {}),
      });
      /* An address can bring back someone removed earlier in this tab: their steps
         must not reopen on the participation that removal destroyed. */
      forgetCompanionReads(client, {
        domainId: event.domainId,
        eventId: event.eventId,
        userId: cree.id,
      });

      /**
       * ⚠️ DÉJÀ INSCRIT PAR QUELQU'UN D'AUTRE : LA ROUTE NE LE RATTACHE PAS. Une adresse
       * connue dont la participation à l'événement porte déjà un « inscrit par » reste
       * sous cet hôte-là (api-v1 `createCompanions`, `isAlreadyRegistered`) : elle
       * n'apparaîtra JAMAIS dans cette liste, filtrée sur `registeredBy2`. Continuer
       * ses étapes ici écrirait sur l'inscription de quelqu'un que ce porteur n'a
       * pas. On le dit, et on s'arrête. Mesuré : un accompagnant « ajouté » qui ne
       * s'affichait pas, sans un mot.
       */
      if (cree.alreadyRegistered) {
        setEnCours(null);
        refuse(
          t(
            "COMPANION_ALREADY_REGISTERED",
            "$1 est déjà inscrit à l'événement sous un autre compte : cette personne ne peut pas être ajoutée à vos accompagnants.",
            [[firstName, name].filter(Boolean).join(" ")],
          ),
        );
        return null;
      }

      /* L'email « après création d'une personne » (action 10), comme la V1 après
         `createInvite` : seulement s'il a une adresse, et sans bloquer la suite. */
      if (email) {
        void sendActionEmail(
          event.domainId,
          event.eventId,
          "companionCreated",
          { userId: cree.id },
          {
            once: true,
            variables: { fromUser: fromUser() },
          },
        ).catch(() => undefined);
      }

      /**
       * ⚠️ LE RESTE DU FORMULAIRE, ET SEULEMENT SUR UN COMPTE NEUF. Une adresse déjà
       * connue rend le compte EXISTANT (`canUpdate: false`) : y écrire les réponses
       * saisies ici renommerait une personne réelle depuis le formulaire d'un tiers. La
       * route l'a rattachée à l'événement, ce qui est le geste demandé ; ses champs
       * restent les siens.
       */
      const allDays = (event.days ?? []).map((d) => d.id);
      const heritage = carrierHeritage({ answers, recorded, all: allDays });
      if (cree.modifiable && fields.length > 0) {
        await saveAnswers({
          domainId: event.domainId,
          target: { eventId: event.eventId, userId: cree.id },
          fields,
          values: values,
          current: {},
          /* Ce que le formulaire ne porte pas et que la V1 écrit aussi : la typologie
             du porteur, dont l'accompagnant HÉRITE, et une présence sur place. */
          base: {
            UE: {
              isRemote: false,
              ...(heritage.typoId !== undefined
                ? { typoId: heritage.typoId }
                : {}),
            },
          },
        });
      } else if (heritage.typoId !== undefined) {
        /**
         * ⚠️ SA TYPOLOGIE, MÊME SANS FORMULAIRE À ÉCRIRE. Un compte déjà connu ou un
         * formulaire sans champ ne passent pas par `saveAnswers` : l'accompagnant restait
         * sans typologie, quand la V1 la lui recopie dans tous les cas
         * (`screens/steps/Informations.jsx`). Seule sa participation est touchée, pas ses
         * nom et prénom.
         */
        await writeCompanionTypo(
          event.domainId,
          event.eventId,
          cree.id,
          heritage.typoId,
        );
      }

      /**
       * ⚠️ ET SES JOURS, QUAND SON PARCOURS NE LES LUI DEMANDE PAS. Un accompagnant
       * « inscrit » l'est à AU MOINS UN JOUR (`funnel/participant.ts`) : sans étape dates
       * (événement d'un seul jour, ou étape coupée par l'organisateur), personne n'écrivait
       * les siens et il ressortait « non inscrit » de tout ce qui recompte, badge et porte
       * compris. Il hérite alors de ceux du porteur — sa typologie l'est déjà, juste
       * au-dessus. Quand l'étape dates existe, c'est ELLE qui les écrit : on ne double pas.
       */
      const withoutDatesStep = !theirFunnel.some((e) => e.key === "dates");
      if (withoutDatesStep && allDays.length > 0) {
        await writeDays(
          event.domainId,
          event.eventId,
          cree.id,
          heritage.days,
          allDays,
        );
      }

      await client.invalidateQueries({ queryKey: ["accompagnants"] });
      setEnCours((a) =>
        a
          ? {
              ...a,
              id: cree.id,
              name: [firstName, name].filter(Boolean).join(" "),
              created: true,
            }
          : a,
      );
      return cree.id;
    } catch (error) {
      /* Sa propre adresse : refusée avant toute écriture. Le formulaire reste ouvert,
         pour corriger l'adresse sans tout ressaisir. */
      if (isOwnEmailRefusal(error)) {
        refuse(
          t(
            "COMPANION_IS_YOU",
            "$1 est votre propre adresse email : indiquez celle de la personne qui vous accompagne, ou laissez le champ vide.",
            [email],
          ),
        );
        return null;
      }
      /* Seul un refus du SERVEUR se lit tel quel. Une erreur levée par nos propres
         modules porte une phrase française écrite en dur, hors dictionnaire : le site
         anglais l'affichait brute (bug #489). Elle prend le mot traduit. */
      refuse(
        error instanceof ApiError && error.message
          ? error.message
          : t(
              "COMPANION_NOT_ADDED",
              "L'accompagnant n'a pas pu être inscrit. Réessayez.",
            ),
      );
      return null;
    }
  };

  /**
   * THEIR COMPANY, WRITTEN AT THE PICK WHEN ONLY ONE IS POSSIBLE.
   *
   * ⚠️ « CHOISIR » REGISTERS THE COWORKER BEFORE THEIR FIRST STEP, and that step writes
   * the company only on « Continuer »: closed with « Annuler », it left them registered
   * under no company, even with nothing to choose. So the company they share with the
   * bearer, or the bearer's only one, is written here, by the rule of the step
   * (`funnel/companionCompany.ts`). Several possible: their journey opens on the company
   * step, which asks, and must. Tells whether they now have a company.
   *
   * ⚠️ READ FROM THE SERVER, NOT FROM THE CACHE: their participation was (re)created a
   * moment ago. The bearer's companies are the exception, they only change through the
   * company step, which keeps that cache up to date.
   */
  const settleCompany = async (personId: number): Promise<boolean> => {
    if (!event || typeof me?.id !== "number") return false;
    const { domainId, eventId } = event;
    const bearerId = me.id;
    const [offered, theirs, record] = await Promise.all([
      client.fetchQuery({
        queryKey: ["companies", domainId, bearerId],
        queryFn: ({ signal }) => fetchMyCompanies(domainId, bearerId, signal),
        staleTime: Infinity,
      }),
      fetchCompanionCompanies(domainId, eventId, personId),
      fetchParticipation(domainId, eventId, personId),
    ]);
    const { current, preset } = companionCompany({
      offered,
      theirs,
      theirEmployeeId: record.employeeId ?? null,
    });
    if (preset === null) return false;
    if (preset === current) return true;
    await attach(domainId, preset, personId, eventId);
    logAction(event, "companyUpdate", {
      userId: personId,
      companyId: preset,
      label: "Modification",
    });
    return true;
  };

  /**
   * INSCRIT UN COLLABORATEUR COMME ACCOMPAGNANT — puis ouvre son parcours.
   *
   * ⚠️ SON PARCOURS S'OUVRE COMME SUR « MODIFIER », PAS COMME SUR UN AJOUT : la personne
   * existe déjà, avec son nom et ses réponses. La V1 fait de même (`resetSteps(5, id)`) :
   * son formulaire s'ouvre rempli de ce qu'elle a, ses étapes suivent.
   *
   * ⚠️ SES JOURS, QUAND SON PARCOURS NE LES DEMANDE PAS : la même règle que `create`, et
   * pour la même raison. Sans jour, il ressortirait « non inscrit » de tout ce qui
   * recompte.
   */
  const choose = async (who: Coworker) => {
    if (!event || typeof me?.id !== "number" || sending) return;
    // ⚠️ Session changée depuis le rendu (autre onglet) : ce qui est ici est à l'ancien compte, rien ne part.
    if (!sameSession(sessionKey)) return;
    setRate(null);
    setSending(true);
    setChoosing(who.id);
    try {
      const allDays = (event.days ?? []).map((d) => d.id);
      const heritage = carrierHeritage({ answers, recorded, all: allDays });
      await attachCoworker(event.domainId, event.eventId, me, who.id, heritage);
      /* Picked again after « Retirer »: their steps must read the participation the
         pick just created, not the one the removal destroyed. */
      forgetCompanionReads(client, {
        domainId: event.domainId,
        eventId: event.eventId,
        userId: who.id,
      });
      /* A failure here is not a failed pick: their journey opens on the company step,
         which reads again and writes the company it shows on « Continuer ». */
      const settled = attachesCompany
        ? await settleCompany(who.id).catch(() => false)
        : true;
      const withoutDatesStep = !theirFunnel.some((e) => e.key === "dates");
      if (withoutDatesStep && allDays.length > 0) {
        await writeDays(
          event.domainId,
          event.eventId,
          who.id,
          heritage.days,
          allDays,
        );
      }
      await client.invalidateQueries({ queryKey: ["accompagnants"] });
      await client.invalidateQueries({ queryKey: ["collaborateurs"] });
      await client.invalidateQueries({ queryKey: ["articles-resume"] });
      setPicking(false);
      setEnCours({
        id: who.id,
        name: who.name,
        step: settled ? informationsAt : 0,
        created: true,
      });
    } catch (error) {
      /* Read in the window, which stays open; on the page if it was closed meanwhile. */
      refuse(
        error instanceof ApiError && error.message
          ? error.message
          : t(
              "COWORKER_NOT_ADDED",
              "$1 n'a pas pu être inscrit. Réessayez.",
              [who.name],
            ),
      );
    } finally {
      setSending(false);
      setChoosing(null);
    }
  };

  const retire = async (who: Companion) => {
    if (!event || typeof me?.id !== "number" || sending) return;
    // ⚠️ Session changée depuis le rendu (autre onglet) : ce qui est ici est à l'ancien compte, rien ne part.
    if (!sameSession(sessionKey)) return;
    setToRemove(null);
    setSending(true);
    /* Their participation is destroyed server side: the coworkers list must offer them
       again. What this tab read of THEM is forgotten when they come back (`create`,
       `choose`), which also covers a removal made elsewhere. */
    const refresh = async () => {
      await client.invalidateQueries({ queryKey: ["accompagnants"] });
      await client.invalidateQueries({ queryKey: ["collaborateurs"] });
    };
    try {
      await removeCompanion(event.domainId, event.eventId, who.id);
      await refresh();
      /* ⚠️ ET LE SOLDE : le serveur facture au porteur tout ce qui est inscrit sous lui.
         Retirer quelqu'un qui portait un billet fait tomber le montant dû — et l'écran
         de paiement continuait à réclamer la somme d'avant. */
      await client.invalidateQueries({ queryKey: ["articles-resume"] });
    } catch {
      const remaining = await fetchCompanions(
        event.domainId,
        event.eventId,
        me.id,
      ).catch(() => null);

      if (remaining === null || remaining.some((a) => a.id === who.id)) {
        refuse(
          t(
            "COMPANION_NOT_REMOVED",
            "Cette personne n'a pas pu être retirée. Réessayez.",
          ),
        );
      } else {
        await refresh();
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="page">
      <div className="ttl" data-cfg="companionsTitle">
        <InPlace settingKey="FO_MEETING_V2_COMPANIONS_TITLE" text={title} as="h2" />
        <InPlace settingKey="FO_MEETING_V2_COMPANIONS_SUBTITLE" text={subtitle} as="p" />
      </div>

      {/* Le bloc libre de l'organisateur, sous le titre : son HTML, assaini comme
          partout (`ui/Prose`). Vide, rien ne s'affiche. */}
      {html.trim() !== "" && (
        <Prose html={html} className="se-prose" data-cfg="companionsTitle" />
      )}

      {/* Not while the coworkers window is open: it shows the refusal itself, and this
          line would be announced a second time from behind it. */}
      {rate && !picking && (
        <p className="loginerr" role="alert" ref={rateRef}>
          {rate.text}
        </p>
      )}

      {remote && (
        <div className="etat" data-cfg="companionsTitle">
          <span className="etat__i" aria-hidden="true">
            <Icon name="verrou" />
          </span>
          <span>
            <b>{t("YOU_ATTEND_REMOTELY", "Vous participez à distance")}</b>
            <small>
              {t(
                "NO_COMPANIONS_FOR_REMOTE",
                "Vous suivez l'événement en ligne : vous ne pouvez pas inscrire d'accompagnant. Pour venir sur place avec quelqu'un, modifiez votre réponse.",
              )}
            </small>
          </span>
        </div>
      )}

      {loading && (
        <p className="empty">
          <span className="spin" aria-hidden="true" />
          <span className="a11y" role="status">
            {t("LOADING", "Chargement")}
          </span>
        </p>
      )}

      {/* Ceux qui sont déjà inscrits. Chacun porte son propre badge, d'où le rang :
          c'est le mot du récapitulatif, et les deux écrans doivent se répondre. */}
      {/**
       * ═══ LA MÊME LISTE QUE LA SOCIÉTÉ ═══
       *
       * Deux étapes, une seule disposition : une carte par ligne, le dessin à gauche, le
       * nom, et les deux gestes en dessous à droite. C'étaient DEUX vocabulaires pour la
       * même chose — `.accfiche` avec ses boutons cerclés sur la même ligne d'un côté,
       * `.opts` avec ses liens en dessous de l'autre — et rien ne le justifiait.
       *
       * ⚠️ LE MOTIF QUI LES SÉPARAIT EST PÉRIMÉ, et c'est pour ça qu'il fallait le
       * relire plutôt que le croire : `.opts` passait en LIGNE sur grand écran dans le
       * PROTOTYPE, ce qui aurait mis les personnes en cartes étroites. La V2 a depuis
       * tranché l'inverse pour tout le tunnel — « une réponse par ligne, du téléphone au
       * grand écran » (`desktop.css`) —, donc l'obstacle n'existe plus.
       */}
      {!enCours && companions.length > 0 && (
        <ul className="opts" data-cfg="companionsTitle">
          {companions.map((a) => (
            <li className="opts__row" key={a.id}>
              {/* ⚠️ UN `div`, PAS UN `button` : on ne CHOISIT pas un accompagnant, on le
                  modifie ou on le retire. Le dessin est celui de la société, le clic
                  non — et `div.opt` reprend la main sur le curseur pour ne pas promettre
                  une action qui n'existe pas. */}
              <div className="opt">
                <span className="opt__i" aria-hidden="true">
                  <Icon name="profil" />
                </span>
                <span className="opt__t">
                  <b>{a.label}</b>
                </span>
                <div className="opts__acts">
                  {/* ⚠️ ON ROUVRE AU FORMULAIRE, PAS À LA SUITE. Modifier quelqu'un,
                    c'est le plus souvent corriger son nom ou son adresse — l'étape
                    qui les porte. Les autres se traversent ensuite, chacune ouverte
                    sur ce qu'il a déjà. The company step, when there is one, stays
                    just behind it, one click back in their thread. */}
                  {!remote && (
                    <button
                      type="button"
                      className="lnk"
                      disabled={sending || companiesPending}
                      onClick={() =>
                        setEnCours({ id: a.id, name: a.name, step: informationsAt })
                      }
                    >
                      {t("EDIT", "Modifier")}
                    </button>
                  )}
                  <button
                    type="button"
                    className="lnk"
                    disabled={sending}
                    onClick={() => setToRemove(a)}
                  >
                    {t("REMOVE", "Retirer")}
                  </button>
                </div>
              </div>
              {/* ⚠️ « BADGE 1 » A ÉTÉ RETIRÉ D'ICI. Le rang ne dit rien à personne : le
                  porteur reconnaît les siens par leur NOM, qui est juste au-dessus, et
                  numéroter des gens n'apporte que du bruit sur une liste déjà courte.
                  Ce que la ligne voulait faire comprendre — chacun a son propre badge —
                  se voit maintenant à sa place, sur l'écran badge, où leurs cartes sont
                  côte à côte. */}
            </li>
          ))}
        </ul>
      )}

      {/* ⚠️ LE PLAFOND SE DIT, IL NE SE SUBIT PAS. Un bouton qui disparaît sans un mot
          se lit comme une panne — surtout après en avoir ajouté un. */}
      {!enCours && !remote && full && (
        <p className="hint" data-cfg="companionsTitle">
          {t(
            "COMPANIONS_CAP_REACHED",
            "Vous avez inscrit le nombre maximum de personnes ($1).",
            [cap],
          )}
        </p>
      )}

      {/* ⚠️ SES ÉTAPES PASSENT DEVANT TOUT LE RESTE. Laisser la liste et le bouton
          d'ajout visibles pendant qu'on remplit pour quelqu'un inviterait à en ajouter
          un second par-dessus le premier, à mi-chemin. */}
      {enCours && (
        <CompanionSteps
          funnel={theirFunnel}
          index={enCours.step}
          companionId={enCours.id}
          name={enCours.name}
          fields={fields}
          words={words}
          onGo={(i) => setEnCours((a) => (a ? { ...a, step: i } : a))}
          onCreate={create}
          /* Whoever is added, by hand or from the list, comes under one of the bearer's
             companies (`funnel/companionCompany.ts`): see `attachesCompany`. */
          attachToCompany={attachesCompany}
          onNext={() => setEnCours((a) => (a ? { ...a, step: a.step + 1 } : a))}
          onDone={() => {
            /* Son parcours est terminé : la confirmation d'accompagnant (action 19),
               celle que la V1 envoie à la confirmation quand `participation === 5`.
               ⚠️ SANS `once`, comme la confirmation du porteur (tâche #221) : rouvert par
               « Modifier » puis terminé, son parcours est une modification, et elle se
               confirme. Le clic est le seul déclencheur, rien ne renvoie au rechargement. */
            if (event && enCours.id !== null) {
              void sendActionEmail(
                event.domainId,
                event.eventId,
                "companion",
                { userId: enCours.id },
                { variables: { fromUser: fromUser() } },
              ).catch(() => undefined);
              /* L'ajout se note quand le porteur a TERMINÉ, sur les deux fiches (le
                 serveur écrit la seconde) ; une modification ne le note pas deux fois. */
              if (enCours.created) logAction(event, "companionAdd", { userId: enCours.id });
            }
            setEnCours(null);
          }}
          /* ⚠️ ET LE REFUS AFFICHÉ S'EN VA AVEC LUI : « Indiquez au moins un nom »
             resté au-dessus de la liste parlerait d'un formulaire qu'on vient de fermer. */
          onCancel={() => {
            setRate(null);
            setEnCours(null);
          }}
        />
      )}

      {/* ⚠️ THE COWORKERS COME BEFORE THE MANUAL ENTRY, which stays: a coworker missing
          from the list is still added with « Ajouter une personne », and their first
          step then asks which of the bearer's companies they come under (none to ask
          with a single company). Their list opens in a window, whatever its length: a
          hundred cards here pushed that button out of sight. */}
      {!enCours && offersCoworkers && !full && (
        <div className="coworkers" data-cfg="companionsTitle">
          <div className="ttl">
            <h3>{t("COWORKERS_TITLE", "Parmi vos collaborateurs")}</h3>
            <p>
              {t(
                "COWORKERS_SUBTITLE",
                "Les personnes de vos sociétés : choisissez celle que vous inscrivez avec vous.",
              )}
            </p>
          </div>
          {coworkersLoading && (
            <p className="empty">
              <span className="spin" aria-hidden="true" />
              <span className="a11y" role="status">
                {t("LOADING", "Chargement")}
              </span>
            </p>
          )}
          {coworkersFailed && (
            <p className="hint">
              {t(
                "COWORKERS_NOT_LOADED",
                "La liste de vos collaborateurs n'a pas pu être chargée. Vous pouvez ajouter la personne à la main.",
              )}
            </p>
          )}
          {!coworkersLoading && !coworkersFailed && coworkers.length === 0 && (
            <p className="hint">
              {t(
                "COWORKERS_NONE",
                "Aucun collaborateur de vos sociétés à proposer. Vous pouvez ajouter la personne à la main.",
              )}
            </p>
          )}
          {coworkers.length > 0 && (
            <div className="opts__add">
              <Button
                type="button"
                variant="ghost"
                loading={companiesPending}
                onClick={() => {
                  setRate(null);
                  setPicking(true);
                }}
              >
                {t("COWORKERS_OPEN", "Choisir parmi mes collaborateurs ($1)", [
                  coworkers.length,
                ])}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* ⚠️ ON ENTRE DANS SON PARCOURS, PAS DANS UN FORMULAIRE NU. Le fil de ses étapes
          se voit dès le premier écran : on sait ce qu'on s'apprête à remplir avant de
          commencer, au lieu de le découvrir après avoir créé quelqu'un. */}
      {!enCours && !remote && !full && (
        <div className="opts__add">
          <Button
            type="button"
            variant="ghost"
            /* Their thread may start with the company step: see `companiesPending`. */
            loading={companiesPending}
            onClick={() => setEnCours({ id: null, name: "", step: 0 })}
          >
            {t("ADD_A_PERSON", "Ajouter une personne")}
          </Button>
        </div>
      )}

      {/* ⚠️ LE TUNNEL SE TAIT PENDANT LE SOUS-PARCOURS. Ses « Retour » et « Continuer »
          restaient à l'écran sous ceux de l'étape en cours : DEUX boutons « Continuer »,
          dont l'un faisait avancer la personne qu'on remplit et l'autre quittait
          l'étape en l'abandonnant à mi-chemin. On ne sort du tunnel qu'une fois sorti de
          la parenthèse. */}
      {/* ⚠️ EN PAGE, IL NE RESTE QUE LE RETOUR. « Continuer » n'a rien à continuer —
          chaque personne est déjà enregistrée à l'ajout — mais quitter la page demande
          un chemin, et le fil d'Ariane n'est pas là pour l'offrir. */}
      {!enCours && asPage && (
        <div className="actions">
          <Button type="button" variant="ghost" onClick={() => go("/")}>
            {t("BACK_TO_HOME", "Retour à l'accueil")}
          </Button>
        </div>
      )}

      {/* ⚠️ ET LA RANGÉE ENTIÈRE DISPARAÎT EN PAGE. Ces deux boutons ne font que
          NAVIGUER : chaque personne est déjà enregistrée à l'ajout. Gardé en page,
          « Continuer » aurait emmené dans le tunnel quelqu'un qui n'y était pas. */}
      {!enCours && !asPage && (
        <div className="actions">
          {previous && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => go(previous.path)}
            >
              {t("BACK", "Retour")}
            </Button>
          )}
          <Button
            type="button"
            disabled={!next}
            onClick={() => next && go(next.path)}
          >
            {t("CONTINUE", "Continuer")}
          </Button>
        </div>
      )}

      <Confirm
        open={toRemove !== null}
        title={t("REMOVE_THIS_PERSON_ASK", "Retirer cette personne ?")}
        message={t(
          "REMOVE_THIS_PERSON_WARNING",
          "$1 ne sera plus inscrit à l'événement, et son badge ne sera plus valable. Son compte, lui, est conservé.",
          [toRemove?.label ?? ""],
        )}
        confirmLabel={t("REMOVE_THEM", "La retirer")}
        onConfirm={() => toRemove && void retire(toRemove)}
        onCancel={() => setToRemove(null)}
        emphasis="cancel"
      />

      {/* Rendered here, outside the `data-cfg` block: hovering the window in organiser
          mode must not point the settings card at the step behind it. */}
      <CoworkerPicker
        open={picking}
        coworkers={coworkers}
        companionIds={companionIds}
        left={cap !== undefined ? cap - companions.length : undefined}
        choosing={choosing}
        error={rate?.text ?? null}
        onChoose={(who) => void choose(who)}
        onAddByHand={() => {
          setRate(null);
          setPicking(false);
          setEnCours({ id: null, name: "", step: 0 });
        }}
        onClose={() => {
          setRate(null);
          setPicking(false);
        }}
      />
    </section>
  );
}
